import { useEffect, useRef, type Dispatch, type MutableRefObject } from "react";
import type { IdentityKeys } from "../../../lib/crypto";
import {
  CHAT_PAGE_SIZE,
  deleteMessageById,
  getMessage,
  listOutboxEntriesForPeer,
  listRecentMessagesForPeer,
  removeOutboxEntry,
  saveMessage,
  type MessageRow,
} from "../../../lib/db";
import {
  apiFetchMessagesByIds,
  apiFetchPinnedMessages,
  apiFetchVaultMessages,
  apiFetchVaultMessagesByIds,
  apiFetchWorkspaceTopicMessages,
  apiMarkWorkspaceTopicRead,
  buildPinnedMessageContextId,
  type ServerMessageRow,
} from "../../../lib/authApi";
import {
  serverWorkspaceMessageToRow,
  workspaceReactionsMapFromServerMessages,
  workspaceTopicPeerKey,
} from "../../../lib/workspaceTopicMessages";
import { notifyWorkspaceTopicListStale } from "../../../lib/workspaceTopicListSync";
import { deriveVaultKey, envelopeReplyToId, vaultPeerKey } from "../../../lib/crypto";
import { vaultServerRowsToMessageRows } from "../../../lib/vaultMessages";
import {
  conversationRefsEqual,
  dmPeerIdFromConversation,
  vaultIdFromConversation,
  type ChatAction,
  type ChatState,
  type ConversationRef,
} from "../../state/chatState";

type DecodeFn = (row: ServerMessageRow) => Promise<MessageRow | null>;

export function useChatConversationEffects(
  chatState: ChatState,
  dispatchChat: Dispatch<ChatAction>,
  authenticated: boolean,
  selectedConversationRef: MutableRefObject<ConversationRef | null>,
  repairSync: () => Promise<void>,
  mergeServerHistory: (peerId: string) => Promise<void>,
  loadPeerMessagesIntoState: (peerId: string, options?: { keepLoadingWhenEmpty?: boolean }) => Promise<void>,
  decodeServerMessageRow: DecodeFn,
  notifyMessagesChanged: (peerId: string) => void,
  currentUserId: string | null,
  keysRef: MutableRefObject<IdentityKeys | null>,
) {
  const replyParentFetchAttemptedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const ref = chatState.selectedConversation;
    let cancelled = false;
    if (ref?.kind === "vault") {
      const vaultId = ref.vaultId;
      void (async () => {
        const keys = keysRef.current;
        const userId = currentUserId;
        if (!keys || !userId) {
          if (cancelled) return;
          const cur0 = selectedConversationRef.current;
          if (cur0?.kind === "vault" && cur0.vaultId === vaultId) {
            dispatchChat({
              type: "set-messages",
              messages: [],
              hasMoreOlder: false,
              conversationLoading: false,
            });
          }
          return;
        }
        try {
          const vaultKey = deriveVaultKey(keys.secretKeyX);
          const { messages: raw } = await apiFetchVaultMessages(vaultId, CHAT_PAGE_SIZE);
          if (cancelled) return;
          const cur = selectedConversationRef.current;
          if (cur?.kind !== "vault" || cur.vaultId !== vaultId) return;
          let rows = await vaultServerRowsToMessageRows(vaultId, userId, raw, vaultKey);
          const peerKey = vaultPeerKey(vaultId);
          const localRows = await listRecentMessagesForPeer(peerKey, 200);
          const serverIds = new Set(rows.map((r) => r.id));
          const pendingMine = localRows.filter(
            (m) => m.from_id === userId && m.pending === true && !serverIds.has(m.id),
          );
          if (pendingMine.length > 0) {
            rows = [...rows, ...pendingMine].sort((a, b) =>
              a.created_at !== b.created_at ? a.created_at - b.created_at : a.id.localeCompare(b.id),
            );
          }
          let hasMoreOlder = false;
          if (raw.length === CHAT_PAGE_SIZE && rows.length > 0) {
            const oldest = rows[0]!;
            try {
              const probe = await apiFetchVaultMessages(vaultId, 1, oldest.created_at, oldest.id);
              if (cancelled) return;
              const cur2 = selectedConversationRef.current;
              if (cur2?.kind !== "vault" || cur2.vaultId !== vaultId) return;
              hasMoreOlder = probe.messages.length > 0;
            } catch {
              if (cancelled) return;
              const curP = selectedConversationRef.current;
              if (curP?.kind !== "vault" || curP.vaultId !== vaultId) return;
              hasMoreOlder = true;
            }
          }
          dispatchChat({ type: "set-error", error: null });
          dispatchChat({
            type: "set-messages",
            messages: rows,
            hasMoreOlder,
            workspaceReactionsByMessageId: {},
          });
        } catch {
          if (cancelled) return;
          const curE = selectedConversationRef.current;
          if (curE?.kind !== "vault" || curE.vaultId !== vaultId) return;
          dispatchChat({
            type: "set-messages",
            messages: [],
            hasMoreOlder: false,
            workspaceReactionsByMessageId: {},
          });
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (ref?.kind === "workspace-topic") {
      const { workspaceId, topicId } = ref;
      void (async () => {
        try {
          const { messages } = await apiFetchWorkspaceTopicMessages(workspaceId, topicId, CHAT_PAGE_SIZE);
          if (cancelled) return;
          const cur = selectedConversationRef.current;
          if (cur?.kind !== "workspace-topic" || cur.workspaceId !== workspaceId || cur.topicId !== topicId) {
            return;
          }
          const serverRows = messages.map((m) => serverWorkspaceMessageToRow(workspaceId, topicId, m));
          let rows = serverRows;
          const peerKey = workspaceTopicPeerKey(workspaceId, topicId);
          if (currentUserId) {
            const outboxEntries = await listOutboxEntriesForPeer(peerKey);
            for (const entry of outboxEntries) {
              if (entry.sentAtLeastOnce) {
                await removeOutboxEntry(entry.id);
                await deleteMessageById(entry.id);
              }
            }
            const localRows = await listRecentMessagesForPeer(peerKey, 200);
            const serverIds = new Set(serverRows.map((r) => r.id));
            const pendingMine = localRows.filter(
              (m) =>
                m.from_id === currentUserId &&
                m.pending === true &&
                !serverIds.has(m.id),
            );
            if (pendingMine.length > 0) {
              rows = [...serverRows, ...pendingMine].sort((a, b) =>
                a.created_at !== b.created_at ? a.created_at - b.created_at : a.id.localeCompare(b.id),
              );
            }
          }
          let hasMoreOlder = false;
          if (messages.length === CHAT_PAGE_SIZE && serverRows.length > 0) {
            const oldest = serverRows[0]!;
            try {
              const probe = await apiFetchWorkspaceTopicMessages(
                workspaceId,
                topicId,
                1,
                oldest.created_at,
                oldest.id,
              );
              if (cancelled) return;
              const cur2 = selectedConversationRef.current;
              if (cur2?.kind !== "workspace-topic" || cur2.workspaceId !== workspaceId || cur2.topicId !== topicId) {
                return;
              }
              hasMoreOlder = probe.messages.length > 0;
            } catch {
              /* Не даём падению probe снести всю загрузку: иначе пустой чат + «вечный» unread в сайдбаре */
              if (cancelled) return;
              const curProbe = selectedConversationRef.current;
              if (curProbe?.kind !== "workspace-topic" || curProbe.workspaceId !== workspaceId || curProbe.topicId !== topicId) {
                return;
              }
              hasMoreOlder = true;
            }
          }
          dispatchChat({ type: "set-error", error: null });
          dispatchChat({
            type: "set-messages",
            messages: rows,
            hasMoreOlder,
            workspaceReactionsByMessageId: workspaceReactionsMapFromServerMessages(messages),
          });
          void apiMarkWorkspaceTopicRead(workspaceId, topicId)
            .then(() => {
              notifyWorkspaceTopicListStale(workspaceId);
            })
            .catch((e: unknown) => {
              console.warn("[styx] mark-read failed:", e);
            });
        } catch {
          if (cancelled) return;
          const cur = selectedConversationRef.current;
          if (cur?.kind !== "workspace-topic" || cur.workspaceId !== workspaceId || cur.topicId !== topicId) {
            return;
          }
          /* Не показываем ошибку — топик может быть пустым или недоступным; показываем пустое состояние */
          dispatchChat({
            type: "set-messages",
            messages: [],
            hasMoreOlder: false,
            workspaceReactionsByMessageId: {},
          });
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    const peerId = dmPeerIdFromConversation(ref);
    if (!peerId) {
      if (!ref) {
        dispatchChat({ type: "set-messages", messages: [], hasMoreOlder: false });
      }
      return;
    }

    /* DM */

    void (async () => {
      await loadPeerMessagesIntoState(peerId, { keepLoadingWhenEmpty: true });
      if (cancelled || dmPeerIdFromConversation(selectedConversationRef.current) !== peerId) return;
      void (async () => {
        await repairSync();
        if (cancelled || dmPeerIdFromConversation(selectedConversationRef.current) !== peerId) return;
        await mergeServerHistory(peerId);
        if (cancelled || dmPeerIdFromConversation(selectedConversationRef.current) !== peerId) return;
        await loadPeerMessagesIntoState(peerId);
      })();
    })();
    return () => {
      cancelled = true;
    };
  }, [
    loadPeerMessagesIntoState,
    mergeServerHistory,
    repairSync,
    chatState.selectedConversation,
    dispatchChat,
    selectedConversationRef,
    currentUserId,
    keysRef,
  ]);

  useEffect(() => {
    const ref = chatState.selectedConversation;
    const uid = currentUserId;
    if (!ref || !uid) {
      dispatchChat({ type: "set-pinned-messages", pins: [] });
      return;
    }
    const { contextType, contextId } = buildPinnedMessageContextId(ref, uid);
    let cancelled = false;
    void apiFetchPinnedMessages(contextType, contextId)
      .then((pins) => {
        if (cancelled) return;
        if (!conversationRefsEqual(selectedConversationRef.current, ref)) return;
        dispatchChat({ type: "set-pinned-messages", pins });
      })
      .catch(() => {
        if (cancelled) return;
        if (!conversationRefsEqual(selectedConversationRef.current, ref)) return;
        dispatchChat({ type: "set-pinned-messages", pins: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [chatState.selectedConversation, currentUserId, dispatchChat, selectedConversationRef]);

  useEffect(() => {
    replyParentFetchAttemptedRef.current = new Set();
  }, [chatState.selectedConversation]);

  useEffect(() => {
    const ref = chatState.selectedConversation;
    const vaultId = vaultIdFromConversation(ref);
    const dmPeer = dmPeerIdFromConversation(ref);
    const peerKey = vaultId ? vaultPeerKey(vaultId) : dmPeer;
    if (!peerKey || !authenticated) return;
    const loaded = new Set(chatState.messages.map((m) => m.id));
    const want = new Set<string>();
    for (const m of chatState.messages) {
      const rid = envelopeReplyToId(m.plaintext);
      if (rid && !loaded.has(rid)) want.add(rid);
    }
    const pending = [...want].filter((id) => !replyParentFetchAttemptedRef.current.has(id));
    if (pending.length === 0) return;

    let cancelled = false;
    void (async () => {
      const stillNeed: string[] = [];
      for (const id of pending) {
        const local = await getMessage(id);
        if (local && local.peerId === peerKey) {
          replyParentFetchAttemptedRef.current.add(id);
          const cur = selectedConversationRef.current;
          const sameVault = cur?.kind === "vault" && vaultId && cur.vaultId === vaultId;
          const sameDm = cur?.kind === "dm" && dmPeer && cur.peerId === dmPeer;
          if (sameVault || sameDm) {
            dispatchChat({ type: "upsert-message", message: local });
          }
        } else {
          stillNeed.push(id);
        }
      }
      if (stillNeed.length === 0) return;
      stillNeed.forEach((id) => replyParentFetchAttemptedRef.current.add(id));
      try {
        const chunkSize = 32;
        for (let i = 0; i < stillNeed.length; i += chunkSize) {
          const chunk = stillNeed.slice(i, i + chunkSize);
          if (cancelled) return;
          if (vaultId) {
            const keys = keysRef.current;
            if (!keys) continue;
            const vaultKey = deriveVaultKey(keys.secretKeyX);
            const { messages: rows } = await apiFetchVaultMessagesByIds(vaultId, chunk);
            if (cancelled) return;
            for (const row of rows) {
              const decodedList = await vaultServerRowsToMessageRows(vaultId, keys.userId, [row], vaultKey);
              const decoded = decodedList[0];
              if (!decoded) continue;
              await saveMessage(decoded);
              const cur = selectedConversationRef.current;
              if (cur?.kind === "vault" && cur.vaultId === vaultId) {
                dispatchChat({ type: "upsert-message", message: decoded });
              }
            }
          } else if (dmPeer) {
            const { messages: rows } = await apiFetchMessagesByIds(dmPeer, chunk);
            if (cancelled) return;
            for (const row of rows) {
              const decoded = await decodeServerMessageRow(row);
              if (!decoded) continue;
              await saveMessage(decoded);
              if (dmPeerIdFromConversation(selectedConversationRef.current) === dmPeer) {
                dispatchChat({ type: "upsert-message", message: decoded });
              }
            }
          }
        }
        notifyMessagesChanged(peerKey);
      } catch {
        stillNeed.forEach((id) => replyParentFetchAttemptedRef.current.delete(id));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    authenticated,
    chatState.messages,
    chatState.selectedConversation,
    decodeServerMessageRow,
    dispatchChat,
    notifyMessagesChanged,
    selectedConversationRef,
    keysRef,
  ]);
}
